import CookieDefinitions from '@amzn/shortbread/dist/definitions/cookie-definitions';
import { DEFAULT_CONSENT } from '../constants/cookies';
import { updateCommon } from './dataLayer';
import { stripOutUnwantedParams } from '../utils/urlParamUtils';
import CookieConsent from './cookieConsent';
import CustomConfig from '../configuration/customConfig';
import { CustomEventData } from '../constants/customEventDataModel';
import { getCookieValue } from '../utils/cookie-utils';

/**
 * Returns the URL of the current page on the browser
 * @return the URL of the current page on the browser
 */
export const getPageURLFromBrowser = (): string => {
  return stripOutUnwantedParams(location.href);
};

/**
 * Returns the URL from customEventData if the event is a custom pageview, or
 * returns the URL of the current page on the browser if the event is not a custom pageview
 * @param customEventData - the data passed in by the custom event currently being processed
 * @return the URL from customEventData if the event is a custom pageview, or
 * returns the URL of the current page on the browser if the event is not a custom pageview
 */
export const getPageURL = (customEventData: CustomEventData | null): string | undefined => {
  if (CustomConfig?.config?.customPageview && customEventData?.event?.type === 'pageview') {
    return customEventData?.page?.pageURL;
  }

  return getPageURLFromBrowser();
};

/**
 * Returns the referrer for the current page
 * @return the referrer for the current page
 */
export const getPageReferer = (): string => {
  return stripOutUnwantedParams(document.referrer);
};

/**
 * Determines how far down the page was scrolled initially.
 * Makes use of the bounding box defined by the selector if one is provided.
 * @param selector - A CSS-style selector for the element that bounds the scrollable portion of the page
 * @return how far down the page was scrolled initially, in pixels
 */
const getInitialScrolledHeight = (selector?: string): number | undefined => {
  if (!selector) {
    return window.scrollY;
  }

  const element = document.querySelector(selector);
  if (element) {
    return element.scrollTop;
  }
};

// Maps cookie name to the browser cookie property in request
const trackedCookiesMap: Record<string, string> = {
  'aws-target-visitor-id': 'awsTargetVisitorId',
  _mkto_trk: 'mrktoTrk',
};

/**
 * Returns the values of the browser cookies defined in trackedCookiesList above
 * @return the values of the browser cookies defined in trackedCookiesList
 */
const getBrowserCookies = (): Record<string, string> => {
  const browserCookies: Record<string, string> = {};

  if (!document || !document.cookie) {
    return browserCookies;
  }

  const cookies = document.cookie.trim().split(';');

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    const cookieParts = cookie.split('=');
    const name = cookieParts[0].trim();

    if (name && trackedCookiesMap[name]) {
      const hasConsent = CookieConsent.hasConsent(name);
      if (hasConsent) {
        const cookiePropertyName = trackedCookiesMap[name];
        const value = decodeURI(cookieParts[1].trim());
        browserCookies[cookiePropertyName] = value;
      }
    }
  }

  return browserCookies;
};

/**
 * Returns the cookie consent with short key names
 * @param cookieConsent - the shortbread ConsentCookie specifying the user's consent.  Defaults to DEFAULT_CONSENT.
 * @return the cookie consent with short key names
 */
export const getCookieConsent = (cookieConsent: CookieDefinitions.ConsentCookie = DEFAULT_CONSENT) => {
  return {
    e: cookieConsent.essential,
    f: cookieConsent.functional,
    p: cookieConsent.performance,
    a: cookieConsent.advertising,
  };
};

/**
 * Sets the relevant data attributes within the Common element in the DataLayer
 * @param selector - A CSS-style selector for the element that bounds the scrollable portion of the page
 */
export const setCommonAttributes = (customEventData: CustomEventData | null, selector?: string) => {
  updateCommon({
    pageURL: getPageURL(customEventData),
    pageTitle: document.title,
    pageLocale: document.documentElement.lang,
    browserViewPortHeight: window.innerHeight,
    browserViewPortWidth: window.innerWidth,
    browserColorDepth: window.screen.colorDepth,
    browserCookies: getBrowserCookies(),
    initialScrolledHeight: getInitialScrolledHeight(selector) || 0,
    regStatus: getCookieValue('regStatus'),
  });
};
